import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Breadcrumbs,
  Link,
  // IconButton,
  // Tooltip,
  useTheme,
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarToday,
  Visibility,
  Timer,
  // Share,
  // FavoriteBorder,
  Category,
  Tag
} from '@mui/icons-material';
import PostSidebar from './PostSidebar';

const PostDetail = ({ post, relatedPosts = [], categories = [], tags = [], loading = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const lng = i18n.language;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString(lng === 'ar' ? 'ar-KW' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // const handleShare = () => {
  //   if (navigator.share) {
  //     navigator.share({
  //       title: post.title?.[lng] || post.title?.en,
  //       text: post.excerpt?.[lng] || post.excerpt?.en,
  //       url: window.location.href,
  //     });
  //   } else {
  //     // Fallback: copy to clipboard
  //     navigator.clipboard.writeText(window.location.href);
  //   }
  // };

  const handleCategoryClick = (category) => {
    navigate(`/blog?category=${category.slug}`);
  };

  const handleTagClick = (tag) => {
    navigate(`/blog?tag=${tag.slug}`);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Box sx={{ height: 400, backgroundColor: 'grey.200', borderRadius: 2, mb: 3 }} />
          <Box sx={{ height: 60, backgroundColor: 'grey.200', borderRadius: 1, mb: 2 }} />
          <Box sx={{ height: 200, backgroundColor: 'grey.200', borderRadius: 1 }} />
        </Box>
      </Container>
    );
  }

  if (!post) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            {t('blog.postNotFound', 'Post not found')}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/" color="inherit">
            {t('common.home', 'Home')}
          </Link>
          <Link component={RouterLink} to="/blog" color="inherit">
            {t('blog.blog', 'Blog')}
          </Link>
          <Typography color="text.primary">
            {post.title?.[lng] || post.title?.en}
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', lg: 'row' } }}>
          {/* Main Content */}
          <Box sx={{ flex: 1 }}>
            {/* Post Header */}
            <Card sx={{
              mb: 3,
              background: theme.palette.mode === 'dark'
                ? 'rgba(26, 26, 26, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 6px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                : '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}>
              <CardContent sx={{ pb: 3 }}>
                {/* Featured Image */}
                {(post.featured_image?.url || post.featured_image_url) && (
                  <Box
                    sx={{
                      width: '100%',
                      height: 400,
                      backgroundImage: `url(${post.featured_image?.url || post.featured_image_url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      borderRadius: 2,
                      mb: 3,
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(0, 0, 0, 0.4)'
                        : '0 4px 12px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                )}

                {/* Post Meta */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  {/* Category */}
                  {post.category && (
                    <Chip
                      icon={<Category sx={{ fontSize: 16 }} />}
                      label={post.category.name?.[lng] || post.category.name?.en}
                      size="small"
                      onClick={() => handleCategoryClick(post.category)}
                      sx={{
                        cursor: 'pointer',
                        background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                        color: 'white',
                        fontWeight: 500,
                      }}
                    />
                  )}

                  {/* Featured Badge */}
                  {post.is_featured && (
                    <Chip
                      label={t('blog.featured', 'Featured')}
                      size="small"
                      sx={{
                        fontWeight: 'bold',
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                        color: 'white',
                      }}
                    />
                  )}
                </Box>

                {/* Title */}
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    lineHeight: 1.2,
                    color: theme.palette.text.primary,
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)'
                      : 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {post.title?.[lng] || post.title?.en}
                </Typography>

                {/* Post Stats */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarToday sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(post.published_at)}
                    </Typography>
                  </Box>

                  {post.reading_time && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Timer sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                      <Typography variant="body2" color="text.secondary">
                        {post.reading_time} {t('blog.minRead', 'min read')}
                      </Typography>
                    </Box>
                  )}

                  {post.view_count && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Visibility sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                      <Typography variant="body2" color="text.secondary">
                        {post.view_count} {t('blog.views', 'views')}
                      </Typography>
                    </Box>
                  )}

                  {/* Author (if available) */}
                  {post.author && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {post.author.name}
                      </Typography>
                    </Box>
                  )}

                  {/* Share Button */}
                  {/* <Tooltip title={t('blog.share', 'Share')}>
                    <IconButton
                      onClick={handleShare}
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.secondary,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                        }
                      }}
                    >
                      <Share />
                    </IconButton>
                  </Tooltip> */}

                  {/* Wishlist Button */}
                  {/* <Tooltip title={t('blog.save', 'Save')}>
                    <IconButton
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.secondary,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                        }
                      }}
                    >
                      <FavoriteBorder />
                    </IconButton>
                  </Tooltip> */}
                </Box>

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                    {post.tags.map((tag) => (
                      <Chip
                        key={tag.id}
                        icon={<Tag sx={{ fontSize: 14 }} />}
                        label={tag.name?.[lng] || tag.name?.en}
                        size="small"
                        variant="outlined"
                        onClick={() => handleTagClick(tag)}
                        sx={{
                          cursor: 'pointer',
                          borderColor: theme.palette.divider,
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            backgroundColor: theme.palette.primary.main + '10',
                          }
                        }}
                      />
                    ))}
                  </Box>
                )}

                <Divider sx={{ mb: 3 }} />

                {/* Post Content */}
                <Box
                  sx={{
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: 1,
                      my: 2,
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(0, 0, 0, 0.4)'
                        : '0 4px 12px rgba(0, 0, 0, 0.1)',
                    },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      mt: 3,
                      mb: 2,
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    },
                    '& p': {
                      mb: 2,
                      lineHeight: 1.7,
                      color: theme.palette.text.primary,
                    },
                    '& blockquote': {
                      borderLeft: `4px solid`,
                      borderLeftColor: 'primary.main',
                      pl: 2,
                      my: 2,
                      fontStyle: 'italic',
                      color: 'text.secondary',
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 215, 0, 0.05)'
                        : 'rgba(255, 215, 0, 0.02)',
                      py: 1,
                      borderRadius: 1,
                    },
                    '& ul, & ol': {
                      pl: 3,
                      mb: 2,
                    },
                    '& li': {
                      mb: 1,
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: post.content?.[lng] || post.content?.en || post.content
                  }}
                />
              </CardContent>
            </Card>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                  {t('blog.relatedPosts', 'Related Posts')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                  {relatedPosts.map((relatedPost) => (
                    <Card
                      key={relatedPost.id}
                      sx={{
                        minWidth: 280,
                        maxWidth: 280,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        },
                      }}
                      onClick={() => navigate(`/blog/${relatedPost.slug}`)}
                    >
                      <Box
                        sx={{
                          height: 140,
                          backgroundImage: `url(${relatedPost.featured_image_url || '/api/placeholder/280/140'})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                      <CardContent sx={{ pb: 2 }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}>
                          {relatedPost.title?.[lng] || relatedPost.title?.en}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(relatedPost.published_at)}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Sidebar */}
          <Box sx={{ width: { lg: 320 }, flexShrink: 0 }}>
            <PostSidebar
              categories={categories}
              tags={tags}
              recentPosts={[]} // Will be populated by parent component
              featuredPosts={relatedPosts.slice(0, 3)}
            />
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default PostDetail;
